import React, { useMemo } from 'react';
import SiteCharts from '../../../components/SiteCharts';
import { volteDropRateCellChartConfig } from '../cellChartConfigs';

/**
 * Props for the VolteDropRateCell component
 */
interface VolteDropRateCellProps {
  /** Callback function to toggle fullscreen mode */
  onFullscreenToggle: () => void;
  /** Current fullscreen state */
  isFullscreen: boolean;
  /** Cell identifier passed to API as cell_name parameter */
  cellName: string;
}

/**
 * VoLTE Drop Rate Chart (LTE Cell Level)
 * 
 * Displays Voice over LTE (VoLTE) drop rate metrics for individual LTE cells:
 * - VoLTE Drop (count)
 * - VoLTE Drop Rate (percentage)
 * 
 * This component is a thin wrapper around the SiteCharts template component,
 * configured with cell-level VoLTE drop metrics from cellChartConfigs.ts
 * 
 * @component
 * @example
 * ```tsx
 * <VolteDropRateCell
 *   onFullscreenToggle={() => setFullscreen(!fullscreen)}
 *   isFullscreen={false}
 * />
 * ```
 * 
 * @see {@link ../cellChartConfigs.ts} - Chart configuration (volteDropRateCellChartConfig)
 * @see {@link ../../../components/SiteCharts.tsx} - Template component
 * @see API: /api/lte-cell/volte-drop-rate?cell_name={cellName}
 */

const VolteDropRateCell: React.FC<VolteDropRateCellProps> = ({ 
  onFullscreenToggle, 
  isFullscreen,
  cellName
}) => {
  // Memoize additionalParams to prevent infinite re-renders
  const additionalParams = useMemo(() => ({ cell_name: cellName }), [cellName]);

  return (
    <SiteCharts 
      config={volteDropRateCellChartConfig}
      onFullscreenToggle={onFullscreenToggle}
      isFullscreen={isFullscreen}
      additionalParams={additionalParams}
    />
  );
};

export default VolteDropRateCell;
